import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { ZoomIn } from '@mui/icons-material'
import { Box, ButtonBase, Card, Checkbox, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { InternalGameScreenshot } from '../../../../api/screenshot-links'
import { ScreenshotThumbnail } from '../../../implementation-examples/components/ScreenshotThumbnail/ScreenshotThumbnail'
import { formatReleaseDate } from '../../helpers/bulkAssignHelpers'

type BulkAssignScreenshotCardProps = {
  screenshot: InternalGameScreenshot
  game?: Game
  selected: boolean
  onToggle: () => void
  onEdit: () => void
  onEnlarge: () => void
}

export const BulkAssignScreenshotCard: FC<BulkAssignScreenshotCardProps> = ({ screenshot, game, selected, onToggle, onEdit, onEnlarge }) => {
  const { t } = useTranslation()
  const gameName = game?.resolvedName || game?.name || screenshot.gameId
  const version = [screenshot.gameVersion || '-', formatReleaseDate(screenshot.versionReleaseDate)].filter(Boolean).join(' - ')
  return (
    <Card
      variant="outlined"
      data-testid={`bulk-assign-screenshot-${screenshot.id}`}
      sx={{ height: '100%', borderColor: selected ? 'primary.main' : undefined, bgcolor: selected ? 'action.selected' : undefined }}
    >
      <Tooltip title={t('internal-analysts:ie_bulk_assign_edit_one')}>
        <ButtonBase
          sx={{ display: 'block', width: '100%' }}
          aria-label={t('internal-analysts:ie_bulk_assign_edit_one_label', { game: gameName, version })}
          onClick={onEdit}
        >
          <ScreenshotThumbnail url={screenshot.url} alt="" height={220} />
        </ButtonBase>
      </Tooltip>
      <Stack direction="row" alignItems="center" spacing={1} p={1}>
        {game && <GameIcon src={game.icons?.length ? game.icons : game.icon ?? undefined} gameName={gameName} size="small" />}
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" fontWeight="bold" noWrap title={gameName}>
            {gameName}
          </Typography>
          {game?.artist && (
            <Typography variant="caption" color="text.secondary" noWrap display="block" title={game.artist}>
              {game.artist}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {t('internal-analysts:analysis_screenshots_version', { version })}
          </Typography>
        </Box>
        <Stack alignItems="center">
          <Checkbox
            size="small"
            checked={selected}
            onChange={onToggle}
            inputProps={{ 'aria-label': t('internal-analysts:ie_bulk_assign_select_label', { game: gameName, version }) }}
          />
          <IconButton size="small" aria-label={t('internal-analysts:ie_bulk_assign_enlarge')} onClick={onEnlarge}>
            <ZoomIn fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  )
}
